import React from 'react';
import styled from '@emotion/styled';
import {
  Box,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { QuickDateRangeType } from '../hooks/useDateSelection';
import { useZoomDetection } from '../hooks/useZoomDetection';
import { DateRangeFields } from '../../../components/inputs/DateRangeFields';
import type { DatePickerProps } from '@mui/x-date-pickers/DatePicker';

const NavArrowButton = styled(Button)`
  && {
    min-width: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    padding: 0;

    &:focus-visible,
    &.Mui-focusVisible {
      outline: 2px solid var(--color-scifree-teal);
      outline-offset: -2px;
    }
  }
`;

interface DateSelectionCardProps {
  startDate: Dayjs;
  endDate: Dayjs;
  dateError: string;
  onStartDateChange: (date: Dayjs | null) => void;
  onEndDateChange: (date: Dayjs | null) => void;
  onQuickDateRange: (type: QuickDateRangeType) => void;
  quickDateRange?: QuickDateRangeType | null;
  onRangeNavigate?: (direction: 'prev' | 'next') => void;
  datePickerSlots: DatePickerProps<Dayjs>['slots'];
}

export const DateSelectionCard: React.FC<DateSelectionCardProps> = ({
  startDate,
  endDate,
  dateError,
  onStartDateChange,
  onEndDateChange,
  onQuickDateRange,
  quickDateRange,
  onRangeNavigate,
  datePickerSlots,
}) => {
  const { t } = useTranslation();
  const today = dayjs().endOf('day');
  const { isVeryCompact } = useZoomDetection();

  return (
    <Box
      sx={{
        mb: 2,
        width: '100%',
        maxWidth: '100%',
        overflow: 'visible',
        pt: 3,
        px: 2,
        pb: '12px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 8,
          mt: 1.5,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            minWidth: 0,
            alignItems: 'center',
            flexWrap: isVeryCompact ? 'wrap' : 'nowrap',
          }}
        >
          {onRangeNavigate && (
            <NavArrowButton
              title={String(t('statistics.navigation.previousPeriod'))}
              onClick={() => onRangeNavigate('prev')}
              variant="secondary"
              aria-label={String(t('statistics.navigation.previousPeriod'))}
              sx={{ alignSelf: { xs: 'center', sm: 'center', md: 'flex-end' } }}
            >
              <ChevronLeftIcon fontSize="small" />
            </NavArrowButton>
          )}

          <DateRangeFields
            direction="row"
            containerSx={{ my: 0 }}
            error={dateError || undefined}
            start={{
              id: 'statistics_start_date',
              label: String(t('statistics.totalGraph.startDate')),
              value: startDate,
              onChange: (date) => date && onStartDateChange(date),
              pickerProps: {
                format: 'YYYY-MM-DD',
                maxDate: today,
                slots: datePickerSlots,
                sx: { width: { xs: 140, sm: 160, md: 180 } },
                slotProps: {
                  textField: {
                    size: 'small',
                    inputProps: {
                      'data-testid': 'statistics-start-date-input',
                    },
                  },
                  openPickerButton: {
                    'data-testid': 'statistics-start-date-open-picker',
                  } as unknown as never,
                },
              },
            }}
            end={{
              id: 'statistics_end_date',
              label: String(t('statistics.totalGraph.endDate')),
              value: endDate,
              onChange: (date) => date && onEndDateChange(date),
              pickerProps: {
                format: 'YYYY-MM-DD',
                minDate: startDate,
                maxDate: today,
                slots: datePickerSlots,
                sx: {
                  width: { xs: 140, sm: 160, md: 180 },
                },
                slotProps: {
                  textField: {
                    size: 'small',
                    inputProps: {
                      'data-testid': 'statistics-end-date-input',
                    },
                  },
                  openPickerButton: {
                    'data-testid': 'statistics-end-date-open-picker',
                  } as unknown as never,
                },
              },
            }}
          />

          {onRangeNavigate && (
            <NavArrowButton
              title={String(t('statistics.navigation.nextPeriod'))}
              onClick={() => onRangeNavigate('next')}
              aria-label={String(t('statistics.navigation.nextPeriod'))}
              variant="secondary"
              sx={{ alignSelf: { xs: 'center', sm: 'center', md: 'flex-end' } }}
            >
              <ChevronRightIcon fontSize="small" />
            </NavArrowButton>
          )}
        </Stack>

        <Box sx={{ flexGrow: 1, minWidth: 0 }} />

        <ToggleButtonGroup
          value={quickDateRange ?? null}
          exclusive
          size="small"
          aria-label="Quick date ranges"
          onChange={(_, newValue: QuickDateRangeType | null) => {
            if (newValue) onQuickDateRange(newValue);
          }}
        >
          {[
            {
              key: '3M',
              label: '3M',
              ariaLabel: 'Set date range to last 3 months',
            },
            {
              key: '6M',
              label: '6M',
              ariaLabel: 'Set date range to last 6 months',
            },
            {
              key: 'YTD',
              label: 'YTD',
              ariaLabel: 'Set date range to year to date',
            },
            {
              key: '1Y',
              label: '1Y',
              ariaLabel: 'Set date range to last 1 year',
            },
          ].map((button) => (
            <ToggleButton
              key={button.key}
              value={button.key}
              aria-label={button.ariaLabel}
            >
              {button.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
};
